import React, {useEffect} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image'; // if you are using next.js, else use <img>
import {JobdetailQr} from '@jobseeker/lotus/assets/images';
import {AppleLogoWhite, PlayStore, Ratings} from '@jobseeker/lotus/assets/icons';
import {
  openUrl,
  getUbaPageName,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';

type RecruiterBannerProps = {
  page?: string;
  title?: string;
  subtitle?: string | any;
  qrSize: number;
  maxWidthLeft?: number;
  QR: any;
};

type ButtonProps = {
  logoSrc: string;
  logoAlt: string;
  first: string;
  second: string;
  forPlayStore: boolean;
  imgWidth: number;
  imgHeight: number;
  handleClick: () => void;
};

const ButtonFun: React.FC<ButtonProps> = ({
  logoSrc,
  logoAlt,
  first,
  second,
  forPlayStore,
  imgWidth,
  imgHeight,
  handleClick,
}) => {
  return (
    <Box
      onClick={handleClick}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      borderRadius={2}
      p={0.75}
      width={86}
      sx={{backgroundColor: 'text.primary', cursor: 'pointer'}}
    >
      <Box sx={{marginRight: '4px'}} display={'flex'}>
        <Image
          src={logoSrc}
          alt={logoAlt}
          width={imgWidth}
          height={imgHeight}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'flex-start'}
      >
        <Typography
          variant="body2"
          fontSize={6}
          color={'#fff'}
          lineHeight={'8px'}
        >
          {first}
        </Typography>
        <Typography
          variant="subtitle2"
          fontSize={9}
          color={'#fff'}
          lineHeight={'8px'}
        >
          {second}
        </Typography>
      </Box>
    </Box>
  );
};
const APP_LINKS = {
  iimjobs: {
    android: 'https://play.google.com/store/apps/details?id=com.org.iimjobs',
    apple:
      'https://apps.apple.com/in/app/iimjobs-management-job-search/id538258273',
    qr: JobdetailQr,
  },
  hirist: {
    android:
      'https://play.google.com/store/apps/details?id=com.hirist.jobseeker&hl=en_IN',
    apple:
      'https://apps.apple.com/in/app/hirist-tech-it-job-search-app/id952697960',
    qr: 'https://static.iimjobs.com/resources/images/hirist_app_link.svg',
  },
};

const AppDownloadBanner: React.FC<RecruiterBannerProps> = ({
  page,
  title,
  subtitle,
  qrSize,
  QR,
  maxWidthLeft = 565,
}) => {
  const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist';
  const linkageName = `${getUbaPageName()} Desktop Download App QR`;
  const linkageType = 'scanQR';

  useEffect(() => {
    TrackEventUBA('appDownloadLinkageView', 'view', {
      platform: PLATFORM,
      linkageName,
      linkageType,
      page: getUbaPageName(),
    });
  }, []);

  const handleStoreClick = (source: 'googlePlay' | 'appStore', url: string) => {
    TrackEventUBA('appDownloadLinkageClick', 'click', {
      source,
      platform: PLATFORM,
      linkageName,
      linkageType,
      page: getUbaPageName(),
    });
    openUrl(url, '_blank');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        bgcolor: 'white',
        px: page == 'applied' ? 2 : 2.75,
        py: page == 'applied' ? 2 : 0.75,
        pb: page == 'applied' ? 2 : 1.25,

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        zIndex: 1,
        '::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '16px',
          padding: '3px',
          paddingTop: '1px',
          background:
            'linear-gradient(270deg, rgba(34,84,72,0.6), rgba(126,159,103,0.6))',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
          zIndex: -1,
        },
      }}
    >
      {/* Left Section */}
      <Box sx={{flex: 1, maxWidth: maxWidthLeft, mt: -0.25}}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{
            backgroundImage: 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            display: 'inline',
            fontSize: page == 'applied' ? {xxs: 14, lg: 16} : 16,
            fontWeight: 600,
          }}
        >
          {title}{' '}
        </Typography>
        <Typography
          sx={{
            mb: 2,
            fontSize: 14,
            fontWeight: 500,
            mt: 0.25,
            lineHeight: '16px',
          }}
        >
          {subtitle}
        </Typography>

        <Grid container alignItems="center" sx={{mt: 1}}>
          <Grid item>
            <ButtonFun
              handleClick={() => {
                handleStoreClick('googlePlay', APP_LINKS[PLATFORM].android);
              }}
              logoSrc={PlayStore}
              logoAlt="playStore"
              first="Get it on"
              second="Google Play"
              forPlayStore={true}
              imgWidth={14}
              imgHeight={14}
            />
          </Grid>
          <Grid item ml={1}>
            <ButtonFun
              handleClick={() => {
                handleStoreClick('appStore', APP_LINKS[PLATFORM].apple);
              }}
              logoSrc={AppleLogoWhite}
              logoAlt="apple"
              first="Download on"
              second="App Store"
              imgWidth={16}
              forPlayStore={false}
              imgHeight={16}
            />
          </Grid>
          <Grid item ml={1}>
            <Typography sx={{fontWeight: 500, fontSize: 12}}>4.6</Typography>
          </Grid>
          <Grid item ml={0.5} mt={-0.5}>
            <Image src={Ratings} width={48} height={8} alt="appPromoQr" />{' '}
          </Grid>
        </Grid>
      </Box>

      {/* Right Section (QR Code) */}
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        mr={{md: 2, lg: 6}}
        maxWidth={page == 'applied' ? {xxs: 120, lg: '100%'} : '100%'}
      >
        <Box
          sx={{
            textAlign: 'center',
            borderRadius: 4.5,
            p: 1.25,
            background:
              'linear-gradient(157deg, rgba(73, 150, 110, 0.10) 0%, rgba(73, 150, 110, 0.08) 100%);',
          }}
        >
          <Box sx={{borderRadius: 3, p: 0.5, pb: 0, backgroundColor: 'white'}}>
            <Image
              src={QR}
              width={qrSize}
              height={qrSize}
              style={{marginBottom: '-4px'}}
              alt="appPromoQr"
            />{' '}
          </Box>
        </Box>
        <Typography
          variant="caption"
          fontWeight="medium"
          display="block"
          mt={1}
          sx={{
            textAlign: 'center',
            backgroundImage:
              'var(--Glory-Gradient, linear-gradient(90deg, #225448 0%, #7E9F67 100%));',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            display: 'inline',
            fontSize: page == 'applied' ? {xxs: 12, lg: 14} : 14,
            fontWeight: 500,
          }}
        >
          Scan this code to download the app!
        </Typography>
      </Box>
    </Box>
    // </Box>
  );
};

export default AppDownloadBanner;
